import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject, combineLatest, filter, first, map, merge, takeUntil } from 'rxjs';
import { FormsPolicyholder, FormsResignation, FormsTravelInPl, FormsTripAbroad, InsuredFormsPolicyholder } from 'src/app/model/forms.model';
import { ProcessType } from 'src/app/model/process-type.model';
import { FormsService } from 'src/app/services/forms.service';
import { PersistStorageKeys, PersistStorageService } from 'src/app/services/persist-storage.service';
import { dateFormatter, formatterCurrency, getCountryZone } from 'src/app/utils';
import { STEPS_CONFIG } from '../../config/step.config';
import { environment } from '../../environments/environment';
import { ApiState } from '../../model/api-state.model';
import { AppType } from '../../model/app-type.model';
import { ProcessService } from '../../services/process.service';
import { AppState } from '../../store';
import * as fromConfig from '../../store/config/config.reducer';
import * as fromProcess from '../../store/process/process.reducer';
import isAppAgentOrTravelAgency from '../../utils/is-agent-or-travel-agency.util';
import { BACKOFFICE_PERMISSION } from '../offer/offer.component';
import { GtmService } from '../../services/gtm.service';

const STEP_2_INDEX = 1;
const STEP_3_INDEX = 2;
const STEP_4_INDEX = 3;

interface InformationsAboutJourneyView {
  title: string;
  value: {
    header: string | number | null | undefined;
    description?: string | number | null | undefined;
  }[];
}

interface RegularView {
  title: string;
  value: string | number | null | undefined;
}

@Component({
  selector: 'app-step5',
  templateUrl: './step5.component.html',
  styleUrls: ['./step5.component.scss'],
})
export class Step5Component implements OnInit, OnDestroy {
  processKind$ = this.store.select(fromProcess.selectProcessKind);
  savedCalculationState$ = this.store.select(fromProcess.selectProcessSavedCalculationState);
  savedCalculationError$ = this.store.select(fromProcess.selectProcessSavedCalculationError);
  showBackEndSection$ = this.store.select(fromProcess.selectSendBackOfficeData);
  selectedTab$ = this.store.select(fromProcess.selectOfferSelectedPackageIndex);
  packages$ = combineLatest([
    this.store.select(fromProcess.selectOfferQuotationData),
    this.store.select(fromProcess.selectProcessKind),
  ]).pipe(map(([data, processKind]) => (processKind ? data?.result.path[processKind]?.packages || [] : [])));
  informationsAboutJourney: InformationsAboutJourneyView[] = [];
  offer: RegularView[] = [];
  yourDetails: RegularView[] = [];
  agentCommissionSplits = this.formsService.agentCommissionSplits;
  backOfficeSection = this.formsService.backofficeSection;
  insureds: InsuredFormsPolicyholder[] =
    this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.insureds ||
    this.formsService.policyholder.controls.insureds.controls.map((control) => ({
      firstName: control.value.firstName as string,
      lastName: control.value.lastName as string,
      dateOfBirth: control.value.dateOfBirth as string,
    }));
  insureds$ = merge(
    this.store.select(fromProcess.selectQuotationInsureds),
    this.store.select(fromProcess.selectPremiumCalculationInsureds),
  ).pipe(filter((insureds) => !!insureds));
  apiStates = ApiState;
  isAppAgentOrTravelAgency = isAppAgentOrTravelAgency();
  isAppTravelAgency = environment.app === AppType.TravelAgency;
  isAppAgent = environment.app === AppType.Agent;
  isBackOfficeAgent = false;
  isPostingOnAgentBehalf = false;
  editLinks = {
    step2: STEPS_CONFIG()[STEP_2_INDEX].route,
    step3: STEPS_CONFIG()[STEP_3_INDEX].route,
    step4: STEPS_CONFIG()[STEP_4_INDEX].route,
  };

  currentPackage$ = combineLatest([this.selectedTab$, this.packages$]).pipe(map(([selectedTab, packages]) => packages[selectedTab]));
  ngDestroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    public formsService: FormsService,
    private translateService: TranslateService,
    private persistStorage: PersistStorageService,
    private processService: ProcessService,
    private gtmService: GtmService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(fromProcess.actions.setStep({ step: 4 }));
    this.gtmService.step5Event();
    this.processService.onPageInit();
    this.isBackOfficeAgent = this.persistStorage.getString(PersistStorageKeys.AgentCategory) === BACKOFFICE_PERMISSION;
    this.isPostingOnAgentBehalf =
      !!this.formsService.backOfficeSectionApi.impersonateAgentNumber &&
      !!this.formsService.backofficeSection.controls.postingOnAgentBehalf.value;

    combineLatest([this.processKind$, this.store.select(fromConfig.selectConfigTripAbroadCountries), this.currentPackage$])
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(([kind, countries, currentPackage]) => {
        if (!kind) {
          return;
        }

        if (kind === ProcessType.TRAVELINPL) {
          const dateFrom = this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.travelDates?.dateFrom || null;
          const dateTo = this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.travelDates?.dateTo || null;
          const numberOfInsureds =
            this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.numberOfInsureds || null;
          const winterSportsHiRisk =
            this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.winterSportsHiRisk || null;
          const winterSportsHiRiskForAllInsureds =
            this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.winterSportsHiRiskForAllInsureds || false;

          this.informationsAboutJourney = [
            {
              title: this.translateService.instant('information-about-journey.destination.title') as string,
              value: [{ header: this.translateService.instant('summary.poland') as string }],
            },
            {
              title: this.translateService.instant(`information-about-journey.travelDates.title`) as string,
              value: [
                {
                  header: `${dateFormatter(dateFrom as string)} - ${dateFormatter(dateTo as string)}`,
                },
              ],
            },
            {
              title: this.translateService.instant(`information-about-journey.numberOfInsureds.title`) as string,
              value: [{ header: numberOfInsureds }],
            },
            {
              title: this.translateService.instant(`information-about-journey.winterSportsHiRisk.title`) as string,
              value: [
                {
                  header: this.translateService.instant(winterSportsHiRisk ? 'common.YES' : 'common.NO') as string,
                },
              ],
            },
          ];

          if (winterSportsHiRisk) {
            this.informationsAboutJourney.push({
              title: this.translateService.instant(`information-about-journey.allHighRisk.title`) as string,
              value: [
                {
                  header: this.translateService.instant(
                    `information-about-journey.allHighRisk.options.${winterSportsHiRiskForAllInsureds?.toString() || ''}`,
                  ) as string,
                },
              ],
            });
          }

          if (this.isAppAgentOrTravelAgency) {
            const withExtremeSports =
              this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.withExtremeSports || null;
            const extremeSportsForAllInsureds =
              this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.extremeSportsForAllInsureds || false;

            this.informationsAboutJourney.push({
              title: this.translateService.instant(`information-about-journey.withExtremeSports.title`) as string,
              value: [
                {
                  header: this.translateService.instant(withExtremeSports ? 'common.YES' : 'common.NO') as string,
                },
              ],
            });

            if (withExtremeSports) {
              this.informationsAboutJourney.push({
                title: this.translateService.instant(`information-about-journey.extremeSportsForAllInsureds.title`) as string,
                value: [
                  {
                    header: this.translateService.instant(
                      `information-about-journey.extremeSportsForAllInsureds.options.${extremeSportsForAllInsureds?.toString() || ''}`,
                    ) as string,
                  },
                ],
              });
            }
          }
        } else if (kind === ProcessType.TRIPABROAD) {
          const destination = this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.destination || null;
          const destinationId = parseInt(destination?.replace('H', '') as string, 10);
          const zone = getCountryZone(destination, countries);
          const dateFrom = this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.travelDates?.dateFrom || null;
          const dateTo = this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.travelDates?.dateTo || null;
          const annual = this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.travelDates?.annual || null;
          const numberOfInsureds =
            this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.numberOfInsureds || null;
          const isPolandIsCurrentResiding =
            this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.isPolandIsCurrentResiding || null;
          const winterSportsHiRisk =
            this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.winterSportsHiRisk || null;
          const winterSportsHiRiskForAllInsureds =
            this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.winterSportsHiRiskForAllInsureds || false;

          this.informationsAboutJourney = [
            {
              title: this.translateService.instant(`information-about-journey.destination.title`) as string,
              value: [
                {
                  header: countries?.find((country) => country.countryId === destinationId)?.countryName,
                  ...(zone
                    ? {
                        description: this.translateService.instant(
                          `information-about-journey.destination.additionalInfo.${
                            this.isAppTravelAgency ? 'travelAgency.' : ''
                          }${zone.toLowerCase()}`,
                        ) as string,
                      }
                    : {}),
                },
              ],
            },
            {
              title: this.translateService.instant(`information-about-journey.travelDates.title`) as string,
              value: [
                {
                  header: `${dateFormatter(dateFrom as string)} ${dateTo ? `– ${dateFormatter(dateTo)}` : ''}`,
                  ...(annual && {
                    description: this.translateService.instant(`information-about-journey.travelDates.annual.label`) as string,
                  }),
                },
              ],
            },
            {
              title: this.translateService.instant(`information-about-journey.numberOfInsureds.title`) as string,
              value: [{ header: numberOfInsureds }],
            },
          ];

          if (this.formsService.isPolandIsCurrentResidingVisible) {
            this.informationsAboutJourney.push({
              title: this.translateService.instant(`information-about-journey.isPolandIsCurrentResiding.title`) as string,
              value: [
                {
                  header: this.translateService.instant(isPolandIsCurrentResiding ? 'common.YES' : 'common.NO') as string,
                },
              ],
            });
          }

          this.informationsAboutJourney.push({
            title: this.translateService.instant(`information-about-journey.winterSportsHiRisk.title`) as string,
            value: [
              {
                header: this.translateService.instant(winterSportsHiRisk ? 'common.YES' : 'common.NO') as string,
              },
            ],
          });

          if (winterSportsHiRisk) {
            this.informationsAboutJourney.push({
              title: this.translateService.instant(`information-about-journey.allHighRisk.title`) as string,
              value: [
                {
                  header: this.translateService.instant(
                    `information-about-journey.allHighRisk.options.${winterSportsHiRiskForAllInsureds?.toString() || ''}`,
                  ) as string,
                },
              ],
            });
          }

          if (this.isAppAgentOrTravelAgency) {
            const withExtremeSports =
              this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.withExtremeSports || null;
            const extremeSportsForAllInsureds =
              this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.extremeSportsForAllInsureds || false;

            this.informationsAboutJourney.push({
              title: this.translateService.instant(`information-about-journey.withExtremeSports.title`) as string,
              value: [
                {
                  header: this.translateService.instant(withExtremeSports ? 'common.YES' : 'common.NO') as string,
                },
              ],
            });

            if (withExtremeSports) {
              this.informationsAboutJourney.push({
                title: this.translateService.instant(`information-about-journey.extremeSportsForAllInsureds.title`) as string,
                value: [
                  {
                    header: this.translateService.instant(
                      `information-about-journey.extremeSportsForAllInsureds.options.${extremeSportsForAllInsureds?.toString() || ''}`,
                    ) as string,
                  },
                ],
              });
            }
          }
        } else if (ProcessType.RESIGNATION) {
          const dateFrom =
            this.persistStorage.getJson<FormsResignation>(PersistStorageKeys.FormsResignation)?.travelDates?.dateFrom || null;
          const dateTo = this.persistStorage.getJson<FormsResignation>(PersistStorageKeys.FormsResignation)?.travelDates?.dateTo || null;
          const numberOfInsureds =
            this.persistStorage.getJson<FormsResignation>(PersistStorageKeys.FormsResignation)?.numberOfInsureds || null;
          const travelCost = this.persistStorage.getJson<FormsResignation>(PersistStorageKeys.FormsResignation)?.travelCost || [];
          const totalTravelPrice =
            this.persistStorage.getJson<FormsResignation>(PersistStorageKeys.FormsResignation)?.totalTravelPrice || [];

          this.informationsAboutJourney = [
            {
              title: this.translateService.instant(`information-about-journey.travelDates.title`) as string,
              value: [
                {
                  header: `${dateFormatter(dateFrom as string)} - ${dateFormatter(dateTo as string)}`,
                },
              ],
            },
            {
              title: this.translateService.instant(`information-about-journey.numberOfInsureds.title`) as string,
              value: [{ header: numberOfInsureds }],
            },
            {
              title: this.translateService.instant(`information-about-journey.travelCost.title`) as string,
              value: travelCost?.map((value) => ({
                header: dateFormatter(value.travelPurchaseDate as string),
                description: formatterCurrency(value.travelCost as number, currentPackage.totalValue.currency),
              })),
            },
            {
              title: this.translateService.instant(`information-about-journey.travelCostSum.title`) as string,
              value: [{ header: formatterCurrency(totalTravelPrice as number, currentPackage.totalValue.currency) }],
            },
          ];
        }

        this.offer = [
          {
            title: this.translateService.instant('summary.packageName') as string,
            value: currentPackage.name,
          },
        ];
        currentPackage.coverages.forEach((coverage) => {
          if (coverage.enabled) {
            this.offer.push({
              title: coverage.name,
              value: !coverage.limit
                ? (this.translateService.instant('offer.offerProtection.turn-on') as string)
                : formatterCurrency(coverage.limit.amount, coverage.limit.currency),
            });
          }

          const isCompany = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.isCompany || null;

          if (!isCompany) {
            const firstName = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.firstName || null;
            const lastName = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.lastName || null;
            const isForeigner = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.isForeigner || null;
            this.yourDetails = [
              {
                title: this.translateService.instant('policyholder.firstName.label') as string,
                value: firstName,
              },
              {
                title: this.translateService.instant('policyholder.lastName.label') as string,
                value: lastName,
              },
            ];
            if (isForeigner) {
              const passport = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.passport || null;
              this.yourDetails.push({
                title: this.translateService.instant('policyholder.passport.label') as string,
                value: passport,
              });
            } else {
              const pesel = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.pesel || null;
              this.yourDetails.push({
                title: this.translateService.instant('policyholder.pesel.label') as string,
                value: pesel,
              });
            }
          } else {
            const companyName = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.companyName || null;
            const nip = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.nip || null;
            const regon = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.regon || null;
            this.yourDetails = [
              {
                title: this.translateService.instant('policyholder.companyName.label') as string,
                value: companyName,
              },
              {
                title: this.translateService.instant('policyholder.nip.label') as string,
                value: nip,
              },
              {
                title: this.translateService.instant('policyholder.regon.label') as string,
                value: regon,
              },
            ];
          }
          const phoneNumber = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.phoneNumber || null;
          const emailAddress = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.emailAddress || null;
          const postCode = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.postCode || null;
          const city = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.city || null;
          const street = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.street || null;
          const houseNumber = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.houseNumber || null;
          const flatNumber = this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.flatNumber || null;
          this.yourDetails = [
            ...this.yourDetails,
            {
              title: this.translateService.instant('policyholder.phoneNumber.label') as string,
              value: phoneNumber,
            },
            {
              title: this.translateService.instant('policyholder.emailAddress.label') as string,
              value: emailAddress,
            },
            {
              title: this.translateService.instant('policyholder.postCode.label') as string,
              value: postCode,
            },
            {
              title: this.translateService.instant('policyholder.city.label') as string,
              value: city,
            },
            {
              title: this.translateService.instant('policyholder.street.label') as string,
              value: street,
            },
            {
              title: this.translateService.instant('policyholder.houseNumber.label') as string,
              value: houseNumber,
            },
            {
              title: this.translateService.instant('policyholder.flatNumber.label') as string,
              value: flatNumber,
            },
          ];
        });
      });
  }

  getPremiumCalculationAddOnNames(i: number): string[] {
    const names: string[] = [];

    combineLatest([this.insureds$, this.selectedTab$, this.packages$])
      .pipe(first())
      .subscribe(([insureds, selectedTab, packages]) => {
        if (insureds) {
          const coverages = insureds[i].coverages.filter((coverage) => coverage.selected);

          coverages.forEach((coverage) => {
            if (coverage.selected) {
              const currentAddon = packages[selectedTab].addOns.find((addOn) => addOn.codeName === coverage.codeName);

              if (currentAddon) {
                names.push(currentAddon.name);
              }
            }
          });
        }
      });

    return names;
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }
}
